import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ship, Plus } from "lucide-react";
import { api, apiError, gbp, num, shortDate } from "@/lib/api";
import { AppShell, PageHeader, Panel, SkeletonRows, EmptyState, Btn, Tag } from "@/components/Shell";

const FLOW = ["ordered", "on_the_water", "arrived", "received"];
const TONE = { ordered: "slate", on_the_water: "violet", arrived: "amber", received: "green" };

export default function Purchasing() {
  const [pos, setPos] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    supplier_name: "Shenzhen Ming Trading",
    currency: "USD",
    fx_rate: 0.79,
    eta_days: 24,
    freight_cost_pence: 120000,
    product_id: "",
    quantity: 200,
    unit_cost: 42.5,
  });

  const load = async () => {
    const [p, prod] = await Promise.all([
      api.get("/purchase-orders"),
      api.get("/products", { params: { page: 1, page_size: 100 } }),
    ]);
    setPos(p.data);
    setProducts(prod.data.items);
    setForm((f) => ({ ...f, product_id: f.product_id || prod.data.items[0]?.id || "" }));
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const create = async () => {
    try {
      await api.post("/purchase-orders", {
        supplier_name: form.supplier_name,
        currency: form.currency,
        fx_rate: Number(form.fx_rate),
        eta_days: Number(form.eta_days),
        freight_cost_pence: Number(form.freight_cost_pence),
        lines: [{ product_id: form.product_id, quantity: Number(form.quantity), unit_cost: Number(form.unit_cost) }],
      });
      toast.success("PO raised — Incoming updated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const advance = async (po) => {
    const next = FLOW[FLOW.indexOf(po.status) + 1];
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/status`, { status: next });
      toast.success(
        next === "received"
          ? `Received — Xero bill created for ${gbp(data.bill_total_pence)}`
          : `${po.number} → ${next.replace(/_/g, " ")}`
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Purchasing & incoming"
        subtitle="Ordered → On the water → Arrived → Received. Foreign costs convert to GBP pence at the stored FX rate; freight lands into moving-average cost."
        testId="purchasing-header"
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <Panel title="Raise a purchase order" testId="po-form-panel" className="xl:col-span-4">
          <div className="space-y-3 p-4">
            <label className="block text-sm">
              Supplier
              <input data-testid="po-supplier-input" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
            </label>
            <label className="block text-sm">
              Product
              <select data-testid="po-product-select" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                className="mono mt-1 w-full border border-slate-300 bg-white px-2 py-2 text-xs focus:border-slate-900 focus:outline-none">
                {products.map((p) => (<option key={p.id} value={p.id}>{p.sku} — {p.name}</option>))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                Quantity
                <input data-testid="po-quantity-input" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
              <label className="block text-sm">
                Unit cost ({form.currency})
                <input data-testid="po-unitcost-input" type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                  className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
              <label className="block text-sm">
                Currency
                <select data-testid="po-currency-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value, fx_rate: e.target.value === "GBP" ? 1 : form.fx_rate })}
                  className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none">
                  {["GBP", "USD", "EUR", "CNY"].map((c) => (<option key={c}>{c}</option>))}
                </select>
              </label>
              <label className="block text-sm">
                FX rate → GBP
                <input data-testid="po-fx-input" type="number" step="0.0001" value={form.fx_rate} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })}
                  className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
              <label className="block text-sm">
                ETA (days)
                <input data-testid="po-eta-input" type="number" value={form.eta_days} onChange={(e) => setForm({ ...form, eta_days: e.target.value })}
                  className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
              <label className="block text-sm">
                Freight (pence)
                <input data-testid="po-freight-input" type="number" value={form.freight_cost_pence} onChange={(e) => setForm({ ...form, freight_cost_pence: e.target.value })}
                  className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" />
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Manual FX entry; the rate used is stored on the receipt movement.
            </p>
            <Btn className="w-full justify-center" data-testid="po-create-button" onClick={create} disabled={!form.product_id}>
              <Plus className="h-4 w-4" /> Raise PO
            </Btn>
          </div>
        </Panel>

        <Panel title="Purchase orders & shipments" testId="po-list-panel" className="xl:col-span-8">
          {!pos ? (
            <SkeletonRows rows={5} />
          ) : pos.length === 0 ? (
            <EmptyState icon={Ship} title="No purchase orders" body="Raise a PO to start tracking incoming stock and ETAs." testId="po-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">PO</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Units</th>
                    <th className="px-3 py-2 text-right">Value (GBP)</th>
                    <th className="px-3 py-2 text-left">ETA</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pos.map((po) => (
                    <tr key={po.id} className="hover:bg-slate-50" data-testid={`po-row-${po.number}`}>
                      <td className="mono px-4 py-2">
                        {po.number}
                        {po.currency !== "GBP" && <span className="ml-1 text-[10px] text-slate-400">{po.currency} @ {po.fx_rate}</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{po.supplier_name}</td>
                      <td className="px-3 py-2">
                        <Tag tone={TONE[po.status]} testId={`po-status-${po.number}`}>{po.status.replace(/_/g, " ")}</Tag>
                        {po.overdue && <Tag tone="red">ETA overdue</Tag>}
                      </td>
                      <td className="num px-3 py-2 text-right">{num(po.lines.reduce((s, l) => s + l.quantity, 0))}</td>
                      <td className="num px-3 py-2 text-right">{gbp(po.total_pence + (po.freight_cost_pence || 0))}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{shortDate(po.eta)}</td>
                      <td className="px-3 py-2 text-right">
                        {po.status !== "received" && (
                          <Btn variant="outline" className="px-2 py-1 text-xs" data-testid={`po-advance-${po.number}`} onClick={() => advance(po)}>
                            Mark {FLOW[FLOW.indexOf(po.status) + 1].replace(/_/g, " ")}
                          </Btn>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
